import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ReloadableResultStatus } from 'api/resultStatus';
import { MergeKey, PaginateJsonSchema } from 'api-clients/monolith';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Pagination } from 'components/Pagination';
import { useDebounce } from 'hooks/useDebounce';

import DeleteMergeKeyDialog from '../DeleteMergeKeyDialog';
import MergeKeyRow from './MergeKeyRow';
import { messages } from './messages';

// ********************************************************************************
// == Type ========================================================================
type FetchData = {
  // eslint-disable-next-line camelcase
  merge_keys: MergeKey[];
  pagination?: PaginateJsonSchema;
};

export type MergeKeysTableState = {
  page: number;
  perPage: number;
  query: string;
  setPage: (n: number) => void;
  setPerPage: (n: number) => void;
  setQuery: (s: string) => void;
};

type Props = {
  accountExternalId: string;
  fetchResult: ReloadableResultStatus<FetchData, unknown>;
  refetch: () => void;
  tableState: MergeKeysTableState;
};

// == Constant ====================================================================
const SEARCH_DEBOUNCE_MS = 300;

const useStyles = makeStyles(theme => ({
  actionsCell: {
    width: 80,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  emptyContainer: {
    color: theme.palette.common.gray700,
    padding: theme.spacing(4, 0),
    textAlign: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
  },
  search: {
    maxWidth: 360,
  },
  toggleCell: {
    width: 120,
  },
}));

// == Component ===================================================================
const MergeKeysTable: VFC<Props> = ({
  accountExternalId,
  fetchResult,
  refetch,
  tableState,
}) => {
  const classes = useStyles();
  const debouncedSearchCommit = useDebounce((value: string) => {
    tableState.setQuery(value);
    tableState.setPage(1);
  }, SEARCH_DEBOUNCE_MS);
  const intl = useIntl();

  // -- State ---------------------------------------------------------------------
  const [keyPendingDelete, setKeyPendingDelete] = useState<MergeKey | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState(tableState.query);

  // -- Handler -------------------------------------------------------------------
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInput(value);
    debouncedSearchCommit(value);
  };

  // -- UI ------------------------------------------------------------------------
  const renderBody = () => {
    if (fetchResult.status === 'loading') {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      );
    }

    if (fetchResult.status === 'error') {
      return (
        <Typography
          className={classes.emptyContainer}
          variant="body2"
          data-testid="merge-keys-table-error"
        >
          <FormattedMessage {...messages.errorLoading} />
        </Typography>
      );
    }

    const { data } = fetchResult;
    const rows = data.merge_keys;

    if (rows.length === 0) {
      if (tableState.query) {
        return (
          <Typography
            className={classes.emptyContainer}
            variant="body2"
            data-testid="merge-keys-table-no-matches"
          >
            <FormattedMessage
              {...messages.noMatches}
              values={{ query: tableState.query }}
            />
          </Typography>
        );
      }
      return (
        <Typography
          className={classes.emptyContainer}
          variant="body2"
          data-testid="merge-keys-table-empty"
        >
          <FormattedMessage {...messages.emptyState} />
        </Typography>
      );
    }

    return (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage {...messages.columnKey} />
                </TableCell>
                <TableCell className={classes.toggleCell}>
                  <FormattedMessage {...messages.columnTranslatable} />
                </TableCell>
                <TableCell className={classes.actionsCell} align="right">
                  <FormattedMessage {...messages.columnActions} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(mergeKeyObject => (
                <MergeKeyRow
                  accountExternalId={accountExternalId}
                  key={mergeKeyObject.external_id}
                  mergeKey={mergeKeyObject}
                  onDeleteRequest={setKeyPendingDelete}
                  onUpdated={refetch}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data.pagination && data.pagination.total_pages > 1 && (
          <Pagination
            currentPage={data.pagination.current_page}
            lastPage={data.pagination.total_pages}
            perPage={data.pagination.per_page}
            setPage={tableState.setPage}
            setPerPage={tableState.setPerPage}
          />
        )}
      </>
    );
  };

  return (
    <div className={classes.container}>
      <TextField
        className={classes.search}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        value={searchInput}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
        inputProps={{
          'aria-label': intl.formatMessage(messages.searchPlaceholder),
        }}
      />
      {renderBody()}
      {keyPendingDelete && (
        <DeleteMergeKeyDialog
          mergeKey={keyPendingDelete}
          accountExternalId={accountExternalId}
          onClose={() => setKeyPendingDelete(null)}
          onDeleted={refetch}
        />
      )}
    </div>
  );
};

// == Export ======================================================================
export default MergeKeysTable;
